import React, { useState } from 'react';
import { Popover } from 'antd';
import { CalendarOutlined, ThunderboltOutlined } from '@ant-design/icons';
import DateTag from '../components/DateTag';
import ActivitiesPopoverContent from '../components/ActivitiesPopoverContent';
import CreateActivityDropdown from '../components/CreateActivityDropdown';
import { ColumnConfig } from '../interfaces/entity';
import { ActivitiesColumnConfig } from '../interfaces/entity';

// Component for rendering activity cell with hover icon
const ActivityCell: React.FC<{
    upcomingInfo: {
        hasUpcoming: boolean;
        upcomingDate: string | Date | null;
    };
    record: any;
    modelName: string;
    color: 'green' | 'red' | 'blue' | 'orange' | 'purple' | 'cyan' | 'yellow' | 'gray';
}> = ({ upcomingInfo, record, modelName, color }) => {
    const [isHovered, setIsHovered] = useState(false);

    // Show DateTag if hasUpcoming is true and date exists
    if (upcomingInfo?.hasUpcoming && upcomingInfo?.upcomingDate) {
        const displayDate = upcomingInfo.upcomingDate;
        const icon = <CalendarOutlined />;

        return (
            <Popover
                content={<ActivitiesPopoverContent recordId={record?._id} modelName={modelName} />}
                trigger="click"
                placement="bottomLeft"
                overlayStyle={{ padding: 0, zIndex: 1050 }}
                overlayClassName="activities-popover"
                getPopupContainer={(triggerNode) => document.body}
                destroyTooltipOnHide={false}
            >
                <div
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                    style={{ cursor: 'pointer', display: 'inline-block' }}
                >
                    <DateTag
                        date={displayDate}
                        icon={icon}
                        color={color}
                    />
                </div>
            </Popover>
        );
    }

    // Show icon on hover when no activities
    return (
        <div
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={(e) => {
                e.stopPropagation();
            }}
            style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                minHeight: 32
            }}
        >
            {isHovered ? (
                <CreateActivityDropdown
                    recordId={record?._id}
                    modelName={modelName}
                    placement="bottomLeft"
                    trigger={
                        <div
                            style={{
                                width: 24,
                                height: 24,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 4,
                                backgroundColor: '#f0f0f0',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                        >
                            <ThunderboltOutlined style={{ fontSize: 14, color: '#1890ff' }} />
                        </div>
                    }
                />
            ) : (
                <div
                    style={{
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.2s'
                    }}
                />
            )}
        </div>
    );
};

/**
 * Generates an Activities column configuration for tables
 * @param config - Configuration for the activities column
 * @returns ColumnConfig object or null if disabled
 */
export const createActivitiesColumn = (
    config?: ActivitiesColumnConfig
): ColumnConfig | null => {
    // If activities column is not enabled, return null
    if (!config?.enabled) {
        return null;
    }

    const {
        modelName = 'leads',
        title = 'Activities',
        width = 120,
        fixed = 'left',
        align = 'center',
        color = 'green'
    } = config;

    return {
        title,
        dataIndex: 'upcomingActivity',
        key: 'upcomingActivity',
        width,
        fixed,
        align,
        render: (
            upcomingInfo: {
                hasUpcoming: boolean;
                upcomingDate: string | Date | null;
            },
            record: any
        ) => {
            return (
                <ActivityCell
                    upcomingInfo={upcomingInfo}
                    record={record}
                    modelName={modelName}
                    color={color}
                />
            );
        },
    };
};
